import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import buildSubjectApiPath from '@Application/Subjects/buildSubjectApiPath';
import SendLiveFragment from '@Application/Beneficiaries/Interviews/LiveTranscription/Commands/SendLiveFragment';
import { del, putFormData } from '@Domain/Api';
import type { CaptureChunk } from '@Domain/Ports/Capture/CapturePort';

export type LiveTranscriptionTarget = {
	readonly facilityId: number;
	readonly subject: SubjectAddress;
	readonly interviewId: number;
	readonly recordingId: number;
};

export type LiveTranscription = {
	/** Queues the fragment behind the ones already sent; never throws. */
	forward: (chunk: CaptureChunk) => void;
	/** Waits for the fragments in flight, then ends the live session. Never throws. */
	close: () => Promise<void>;
};

/**
 * Sends the fragments of one recording to the live transcription, one at a time and in order. The
 * live path is best effort: nothing it does is ever reported, a failed fragment is dropped, and a
 * `null` target or a `live: false` answer stops every later send.
 */
const openLiveTranscription = (target: Promise<LiveTranscriptionTarget | null>): LiveTranscription => {
	let isLive = true;
	let chain: Promise<void> = Promise.resolve();
	let resolvedTarget: LiveTranscriptionTarget | null = null;

	const recordingUrl = (live: LiveTranscriptionTarget): string => {
		return `${buildSubjectApiPath(live.facilityId, live.subject)}/interviews/${live.interviewId}/recordings/${live.recordingId}`;
	};

	const send = async (chunk: CaptureChunk): Promise<void> => {
		try {
			resolvedTarget ??= await target;

			if (resolvedTarget === null || !isLive) {
				isLive = false;
				return;
			}

			const fragment = new SendLiveFragment(chunk.segment, chunk.index, chunk.blob);
			const answer = await putFormData<{ live: boolean }>(
				`${recordingUrl(resolvedTarget)}/segments/${fragment.segment}/live-fragments/${fragment.index}`,
				fragment.toFormData(),
			);

			isLive = answer.live;
		} catch {
			// A fragment lost on the live path costs nothing: the recording itself is transferred whole.
		}
	};

	const forward = (chunk: CaptureChunk): void => {
		if (!isLive) {
			return;
		}

		chain = chain.then(() => send(chunk));
	};

	const close = async (): Promise<void> => {
		await chain;

		try {
			const live = resolvedTarget ?? (await target);

			if (live !== null) {
				await del<void>(`${recordingUrl(live)}/live-session`);
			}
		} catch {
			// The server ends an abandoned live session on its own.
		}
	};

	return { forward, close };
};

export default openLiveTranscription;
